import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const ASSET_BASE = BACKEND_URL;

// Format episode number: integer values show as "1", sub-episodes as "1.1"
export function formatEpisodeNumber(n) {
  if (n == null || n === "") return "";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return Number.isInteger(num) ? String(num) : String(num).replace(/\.?0+$/, "");
}

// Compact TV-guide-style duration formatter: "22:00" → 22", "11:30" → 11",
// "1:22:00" → 82", "22" → 22". Truncates to whole minutes and appends a
// double-prime symbol (as used in classic broadcast schedules).
export function formatDurationShort(raw) {
  if (!raw) return "";
  const parts = String(raw).split(":").map((p) => parseInt(p, 10)).filter((n) => !Number.isNaN(n));
  if (parts.length === 0) return "";
  let minutes;
  if (parts.length === 1) minutes = parts[0];
  else if (parts.length === 2) minutes = parts[0];
  else minutes = parts[0] * 60 + parts[1];
  if (!minutes || minutes < 1) return "";
  return `${minutes}\u2033`;
}

// Parse a duration string ("22:00", "11:30", "1:22:00", "22") into seconds.
// Returns 0 for invalid inputs so the caller can decide whether to fall back.
export function parseDurationToSec(raw) {
  if (!raw) return 0;
  const parts = String(raw).split(":").map((p) => parseInt(p, 10)).filter((n) => !Number.isNaN(n));
  if (parts.length === 0) return 0;
  if (parts.length === 1) return parts[0] * 60;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] * 3600 + parts[1] * 60 + parts[2];
}

// Split "A / B" episode titles so the segment after the slash renders on a
// new line. Preserves the whole title when there is no slash.
export function splitEpisodeTitle(title) {
  if (!title || typeof title !== "string") return { primary: title || "", secondary: null };
  const idx = title.indexOf("/");
  if (idx < 0) return { primary: title, secondary: null };
  return {
    primary: title.slice(0, idx).trim(),
    secondary: title.slice(idx + 1).trim() || null,
  };
}

// Format an episode-view counter for compact display next to the meta line.
// Uses Romanian short suffixes: k for thousands, M for millions. Numbers under
// 1000 render as-is. Nothing renders when the count is missing/zero — the
// meta line is more readable without a "0 vizualizări" leftover.
export function formatViews(n) {
  const v = Number(n) || 0;
  if (v <= 0) return "";
  if (v < 1000) return `${v}`;
  if (v < 10_000) return `${(v / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  if (v < 1_000_000) return `${Math.round(v / 1000)}k`;
  return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
}

// Convert any image/file URL (full http, /api/uploads/x, or legacy /uploads/x)
// into an absolute URL the browser can actually fetch through the K8s ingress.
export function resolveAssetUrl(u) {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://") || u.startsWith("data:")) return u;
  // Legacy uploads (saved before we moved to /api/uploads/) need the /api prefix.
  if (u.startsWith("/uploads/")) return `${BACKEND_URL}/api${u}`;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return `${BACKEND_URL}${u}`;
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Auto-refresh access token on 401, then retry the original request once.
let refreshPromise = null;

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const isAuthEndpoint =
      original?.url?.includes("/auth/login") ||
      original?.url?.includes("/auth/register") ||
      original?.url?.includes("/auth/refresh") ||
      original?.url?.includes("/auth/logout");

    if (status === 401 && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = api.post("/auth/refresh").finally(() => {
            refreshPromise = null;
          });
        }
        await refreshPromise;
        return api(original);
      } catch (e) {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Ceva nu a mers bine. Încearcă din nou.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
